import { supabase } from '../../supabase';

/**
 * Wrapper unico per le RPC di sessione dei portali.
 *
 * I tre portali (cliente, collaboratore, partner) girano con il ruolo `anon`: prima di questo
 * livello ogni lettura e ogni scrittura partiva come `.from(...)` senza mandare al database
 * alcuna prova di sessione, e le policy anon erano quindi scritte come "tutto ciò che è
 * condiviso in un portale qualsiasi". Da qui in avanti ogni operazione passa da una funzione
 * SECURITY DEFINER che riceve il token di sessione (lo stesso già in localStorage, già usato
 * da *_validate_session) e restituisce soltanto i dati legati a quell'accesso.
 *
 * Contratto lato database: supabase/migrations/20260802_portali_rpc_sessione.sql
 */

export type PortalKind = 'client' | 'collaborator' | 'partner';

/** Sessione assente, scaduta o accesso disattivato. SQLSTATE 28000. */
export const PORTAL_SESSION_INVALID = 'PORTAL_SESSION_INVALID';

export class PortalSessionExpiredError extends Error {
  readonly portal: PortalKind;

  constructor(portal: PortalKind) {
    super('Sessione del portale non valida o scaduta');
    this.name = 'PortalSessionExpiredError';
    this.portal = portal;
  }
}

/** Errore applicativo della RPC diverso dalla sessione scaduta (permesso, risorsa non condivisa). */
export class PortalRpcError extends Error {
  readonly sqlState: string;
  readonly portalCode: string;
  readonly fn: string;
  /**
   * `hint` di PostgreSQL. Le RPC dei portali ci mettono il NOME DEL CAMPO che ha fatto
   * fallire la chiamata (PORTAL_FIELD_NOT_WRITABLE, PORTAL_TEXT_TOO_LONG,
   * BRIEF_DOCUMENT_PATH_*): senza questo l'interfaccia può solo dire "riprova".
   */
  readonly hint: string | null;

  constructor(
    fn: string,
    portalCode: string,
    sqlState: string,
    message: string,
    hint?: string | null
  ) {
    super(message);
    this.name = 'PortalRpcError';
    this.fn = fn;
    this.portalCode = portalCode;
    this.sqlState = sqlState;
    this.hint = hint ?? null;
  }
}

type SessionExpiredHandler = () => void;

const expiredHandlers = new Map<PortalKind, SessionExpiredHandler>();

/**
 * Registrato dagli store dei portali: alla scadenza della sessione lo stato viene azzerato,
 * così le guard di rotta rimandano al login invece di mostrare pagine vuote.
 */
export function onPortalSessionExpired(portal: PortalKind, handler: SessionExpiredHandler): void {
  expiredHandlers.set(portal, handler);
}

function notifyExpired(portal: PortalKind): void {
  const handler = expiredHandlers.get(portal);
  if (!handler) return;
  try {
    handler();
  } catch (err) {
    console.error('[portalRpc] Errore nella gestione della sessione scaduta:', err);
  }
}

/** Le RPC sollevano eccezioni con messaggi del tipo PORTAL_PROJECT_NOT_SHARED. */
function extractPortalCode(message?: string | null): string {
  if (!message) return '';
  const match = message.match(/PORTAL_[A-Z_]+/);
  return match ? match[0] : '';
}

export function isPortalSessionExpired(error: unknown): boolean {
  return error instanceof PortalSessionExpiredError;
}

/**
 * Esegue una RPC del portale passando sempre il token di sessione.
 *
 * Il chiamante non deve mai costruire query che presuppongono l'accesso a un progetto o a un
 * task: gli id vengono passati come parametro e verificati lato server.
 */
export async function portalRpc<T>(
  portal: PortalKind,
  fn: string,
  token: string | null | undefined,
  params: Record<string, unknown> = {}
): Promise<T> {
  if (!token) {
    notifyExpired(portal);
    throw new PortalSessionExpiredError(portal);
  }

  const { data, error } = await supabase.rpc(fn, { p_token: token, ...params });

  if (error) {
    const portalCode = extractPortalCode(error.message);
    const sqlState = (error as { code?: string }).code || '';

    if (portalCode === PORTAL_SESSION_INVALID || sqlState === '28000') {
      notifyExpired(portal);
      throw new PortalSessionExpiredError(portal);
    }

    const hint = (error as { hint?: string | null }).hint ?? null;
    throw new PortalRpcError(
      fn,
      portalCode,
      sqlState,
      error.message || 'Errore del portale',
      hint
    );
  }

  return data as T;
}

/** Le RPC che restituiscono elenchi usano coalesce(..., '[]'), ma il null resta possibile. */
export function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}
